import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SectionCard, InfoNote } from "@/components/common/Cards";
import { Field, MoneyInput, NumInput } from "@/components/common/Inputs";
import { Sel } from "@/components/common/Sel";
import { CAPEX_CATEGORIES, newCapex } from "@/lib/engines/isp";
import { fmtNum, fmtRp } from "@/lib/format";

export default function CapexPanel({ capex, setCapex, readOnly }) {
  const add = () => setCapex((c) => [...c, newCapex()]);
  const remove = (id) => setCapex((c) => c.filter((x) => x.id !== id));
  const update = (id, patch) => setCapex((c) => c.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const total = capex.reduce((a, c) => a + (c.qty || 0) * (c.unitPrice || 0), 0);
  const monthly = capex.reduce((a, c) => a + ((c.qty || 0) * (c.unitPrice || 0)) / Math.max(1, c.lifeMonths || 1), 0);

  return (
    <SectionCard
      title="CAPEX"
      eyebrow="Belanja modal wilayah"
      description="Depresiasi dihitung per-item dengan masa manfaat (bulan). Total CAPEX dan depresiasi bulanan akan dipakai sebagai biaya tetap."
      actions={
        <Button onClick={add} disabled={readOnly} size="sm" data-testid="isp-capex-add">
          <Plus className="h-4 w-4" /> Tambah item
        </Button>
      }
      bodyClassName="p-0"
      testid="isp-capex-card"
    >
      <div className="overflow-x-auto scroll-gold">
        <table className="dense-table w-full text-xs" data-testid="isp-capex-table">
          <thead>
            <tr className="bg-secondary">
              <th className="text-left px-4 py-3 w-[180px]">Kategori</th>
              <th className="text-left px-3 py-3">Nama item</th>
              <th className="text-right px-3 py-3 w-[90px]">Qty</th>
              <th className="text-right px-3 py-3 w-[180px]">Harga satuan</th>
              <th className="text-right px-3 py-3 w-[110px]">Umur (bln)</th>
              <th className="text-right px-3 py-3 w-[150px]">Total</th>
              <th className="text-right px-3 py-3 w-[150px]">Depresiasi/bln</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {capex.map((c) => {
              const subtotal = (c.qty || 0) * (c.unitPrice || 0);
              const dep = subtotal / Math.max(1, c.lifeMonths || 1);
              return (
                <tr key={c.id} className="border-t border-border hover:bg-[hsl(220_10%_9%)]" data-testid={`isp-capex-row-${c.id}`}>
                  <td className="px-4 py-2">
                    <Sel
                      value={c.category}
                      onChange={(v) => update(c.id, { category: v })}
                      options={CAPEX_CATEGORIES.map((x) => ({ value: x, label: x }))}
                      testid={`isp-capex-category-${c.id}`}
                      disabled={readOnly}
                      className="h-9"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input value={c.name} onChange={(e) => update(c.id, { name: e.target.value })} className="h-9 bg-[hsl(220_12%_8%)]" disabled={readOnly} data-testid={`isp-capex-name-${c.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <NumInput value={c.qty} onChange={(v) => update(c.id, { qty: v })} decimals={0} disabled={readOnly} testid={`isp-capex-qty-${c.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <MoneyInput value={c.unitPrice} onChange={(v) => update(c.id, { unitPrice: v })} disabled={readOnly} testid={`isp-capex-price-${c.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <NumInput value={c.lifeMonths} onChange={(v) => update(c.id, { lifeMonths: v })} decimals={0} disabled={readOnly} testid={`isp-capex-life-${c.id}`} />
                  </td>
                  <td className="px-3 py-2 text-right num">{fmtRp(subtotal)}</td>
                  <td className="px-3 py-2 text-right num text-primary">{fmtRp(dep)}</td>
                  <td className="px-3 py-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[hsl(0_72%_62%)]" onClick={() => remove(c.id)} disabled={readOnly} data-testid={`isp-capex-remove-${c.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {capex.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-muted-foreground">Belum ada item CAPEX.</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-secondary border-t border-border">
              <td colSpan={5} className="px-4 py-3 font-semibold">Total CAPEX</td>
              <td className="px-3 py-3 text-right num font-semibold">{fmtRp(total)}</td>
              <td className="px-3 py-3 text-right num text-primary font-semibold" data-testid="isp-capex-total-dep">{fmtRp(monthly)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="p-4">
        <InfoNote>
          Umur manfaat default: OLT/router/server 60 bulan, kabel FO & ODP 120 bulan, perizinan 60 bulan. Sesuaikan jika kebijakan akuntansi berbeda.
        </InfoNote>
      </div>
    </SectionCard>
  );
}
